import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  type DevelopmentGateStatus,
  useDevelopmentGate,
  useSaveDevelopmentGate,
} from "../api/developmentGate";
import type { Role } from "../api/types";
import { useNow } from "../time/useNow";
import { FeedbackLine } from "../ui/FeedbackLine";

type Mode = "off" | "indefinite" | "timed";
interface Draft {
  mode: Mode;
  hours: number | string;
  reschedule: boolean;
  allowed_levels: Role[];
}

function gateDraft(current: DevelopmentGateStatus): Draft {
  return {
    mode: !current.enabled ? "off" : current.ends_at ? "timed" : "indefinite",
    hours: 1,
    reschedule: false,
    allowed_levels: current.allowed_levels,
  };
}

function sameGate(left: DevelopmentGateStatus, right: DevelopmentGateStatus): boolean {
  return (
    left.enabled === right.enabled &&
    left.ends_at === right.ends_at &&
    left.allowed_levels.length === right.allowed_levels.length &&
    left.allowed_levels.every((role, index) => role === right.allowed_levels[index])
  );
}

export function DevelopmentGatePanel() {
  const gate = useDevelopmentGate();
  const { t } = useTranslation("admin");
  const current = gate.data;

  return (
    <Paper p="md" mb={26} maw={900}>
      <Stack gap="md">
        <Group justify="space-between" gap="xs">
          <Title order={3} fz="sm">
            {t("developmentGate.heading")}
          </Title>
          {current && (
            <Badge color={current.enabled ? "warn" : "accent"}>
              {t(current.enabled ? "developmentGate.closed" : "developmentGate.open")}
            </Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed" maw="75ch">
          {t("developmentGate.description")}
        </Text>
        {gate.isPending ? (
          <Skeleton height={100} />
        ) : gate.isError ? (
          <Alert color="danger">
            <Stack gap="xs">
              <Text size="sm">{gate.error.message}</Text>
              <Button onClick={() => void gate.refetch()}>{t("developmentGate.retry")}</Button>
            </Stack>
          </Alert>
        ) : (
          current && <DevelopmentGateForm current={current} />
        )}
      </Stack>
    </Paper>
  );
}

function DevelopmentGateForm({ current }: { current: DevelopmentGateStatus }) {
  const save = useSaveDevelopmentGate();
  const { t, i18n } = useTranslation("admin");
  const [draft, setDraft] = useState<{ base: DevelopmentGateStatus; value: Draft } | null>(null);
  const [saved, setSaved] = useState(false);
  const now = useNow(60_000);
  const values = draft?.value ?? gateDraft(current);
  const conflict = draft !== null && !sameGate(draft.base, current);
  const hours = Number(values.hours);
  const needsDuration = values.mode === "timed" && (values.reschedule || !current.ends_at);
  const validDuration =
    Number.isFinite(hours) &&
    hours > 0 &&
    Number.isFinite(new Date(now + hours * 3_600_000).getTime());

  function change(update: Partial<Draft>) {
    setDraft((held) => ({
      base: held?.base ?? current,
      value: { ...(held?.value ?? values), ...update },
    }));
    setSaved(false);
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (conflict || (needsDuration && !validDuration)) return;
        save.mutate(
          {
            enabled: values.mode !== "off",
            ends_at:
              values.mode !== "timed"
                ? null
                : needsDuration
                  ? new Date(Date.now() + hours * 3_600_000).toISOString()
                  : current.ends_at,
            allowed_levels: ["admin", ...values.allowed_levels.filter((role) => role !== "admin")],
          },
          {
            onSuccess: () => {
              setDraft(null);
              setSaved(true);
            },
          },
        );
      }}
    >
      <Stack gap="md">
        <Select
          label={t("developmentGate.mode")}
          value={values.mode}
          onChange={(mode) => {
            if (mode) change({ mode: mode as Mode, reschedule: mode === "timed" });
          }}
          data={[
            { value: "off", label: t("developmentGate.off") },
            { value: "indefinite", label: t("developmentGate.indefinite") },
            { value: "timed", label: t("developmentGate.timed") },
          ]}
          maw={420}
          disabled={save.isPending}
        />
        {values.mode === "timed" && (
          <Stack gap={6} maw={420}>
            <NumberInput
              label={t("developmentGate.duration")}
              description={t("developmentGate.durationHint")}
              min={0.01}
              step={0.5}
              decimalScale={2}
              value={values.hours}
              onChange={(hours) => change({ hours, reschedule: true })}
              disabled={save.isPending}
              error={
                needsDuration && !validDuration ? t("developmentGate.invalidDuration") : undefined
              }
            />
            {current.enabled && current.ends_at && (
              <Text size="xs" c="dimmed">
                {t("developmentGate.currentUntil", {
                  when: new Date(current.ends_at).toLocaleString(i18n.resolvedLanguage, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }),
                })}
              </Text>
            )}
          </Stack>
        )}
        <Checkbox.Group
          label={t("developmentGate.roles")}
          description={t("developmentGate.rolesHint")}
          value={["admin", ...values.allowed_levels.filter((role) => role !== "admin")]}
          onChange={(roles) => change({ allowed_levels: roles as Role[] })}
        >
          <Group mt="xs" gap="lg">
            <Checkbox value="admin" label={t("developmentGate.admin")} disabled />
            <Checkbox
              value="editor"
              label={t("developmentGate.editor")}
              disabled={save.isPending}
            />
            <Checkbox
              value="viewer"
              label={t("developmentGate.viewer")}
              disabled={save.isPending}
            />
          </Group>
        </Checkbox.Group>
        <Text size="xs" c="dimmed">
          {t("developmentGate.adminAlways")}
        </Text>
        <FeedbackLine role={save.isError ? "alert" : "status"}>
          {save.isError && <Text inherit>{save.error.message}</Text>}
          {saved && (
            <Text inherit c="accent">
              {t("developmentGate.saved")}
            </Text>
          )}
          {conflict && (
            <Text inherit c="warn">
              {t("developmentGate.serverChanged")}
            </Text>
          )}
        </FeedbackLine>
        <Group gap="xs">
          <Button
            type="submit"
            variant="filled"
            loading={save.isPending}
            disabled={!draft || conflict || (needsDuration && !validDuration)}
          >
            {t("developmentGate.save")}
          </Button>
          {draft &&
            (conflict ? (
              <>
                <Button
                  disabled={save.isPending}
                  onClick={() => {
                    setDraft(null);
                    setSaved(false);
                  }}
                >
                  {t("developmentGate.useServerVersion")}
                </Button>
                <Button
                  disabled={save.isPending}
                  onClick={() => setDraft((held) => (held ? { ...held, base: current } : held))}
                >
                  {t("developmentGate.keepDraft")}
                </Button>
              </>
            ) : (
              <Button
                disabled={save.isPending}
                onClick={() => {
                  setDraft(null);
                  setSaved(false);
                }}
              >
                {t("developmentGate.cancel")}
              </Button>
            ))}
        </Group>
      </Stack>
    </form>
  );
}
